import React from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "./ui/button";
import { cn } from "../lib/utils";

interface Holiday {
  _id: string;
  date: string;
  name: string;
  year?: number;
  state?: string;
  isActive?: boolean;
}

interface HolidayCalendarProps {
  holidays: Holiday[];
  currentDate: Date;
  onDateChange: (date: Date) => void;
  onDateClick?: (date: Date, holiday?: Holiday) => void;
  readOnly?: boolean;
}

export function HolidayCalendar({
  holidays,
  currentDate,
  onDateChange,
  onDateClick,
  readOnly = false,
}: HolidayCalendarProps) {
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();

  // Get first day of month and number of days
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  // Create a map of holidays by date string (YYYY-MM-DD)
  // Include both active and inactive holidays so inactive ones can be edited
  const holidaysMap = new Map<string, Holiday>();
  holidays.forEach((holiday) => {
    // Handle both ISO string format and YYYY-MM-DD format
    let dateStr: string;
    if (
      typeof holiday.date === "string" &&
      holiday.date.match(/^\d{4}-\d{2}-\d{2}$/)
    ) {
      // Already in YYYY-MM-DD format
      dateStr = holiday.date;
    } else {
      // Convert to YYYY-MM-DD format
      const date = new Date(holiday.date);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      dateStr = `${year}-${month}-${day}`;
    }
    holidaysMap.set(dateStr, holiday);
  });

  const monthNames = [
    "January",
    "February",
    "March",
    "April",
    "May",
    "June",
    "July",
    "August",
    "September",
    "October",
    "November",
    "December",
  ];

  const dayNames = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

  const goToPreviousMonth = () => {
    onDateChange(new Date(year, month - 1, 1));
  };

  const goToNextMonth = () => {
    onDateChange(new Date(year, month + 1, 1));
  };

  const goToToday = () => {
    onDateChange(new Date());
  };

  const handleDateClick = (day: number) => {
    const clickedDate = new Date(year, month, day);
    const dateStr = formatDateToString(year, month, day);
    const holiday = holidaysMap.get(dateStr);
    if (onDateClick) {
      onDateClick(clickedDate, holiday);
    }
  };

  const isToday = (day: number) => {
    const today = new Date();
    return (
      day === today.getDate() &&
      month === today.getMonth() &&
      year === today.getFullYear()
    );
  };

  const formatDateToString = (
    year: number,
    month: number,
    day: number
  ): string => {
    const date = new Date(year, month, day);
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, "0");
    const d = String(date.getDate()).padStart(2, "0");
    return `${y}-${m}-${d}`;
  };

  const isHoliday = (day: number) => {
    const dateStr = formatDateToString(year, month, day);
    return holidaysMap.has(dateStr);
  };

  const getHoliday = (day: number): Holiday | undefined => {
    const dateStr = formatDateToString(year, month, day);
    return holidaysMap.get(dateStr);
  };

  // Generate calendar days
  const calendarDays: (number | null)[] = [];
  // Add empty cells for days before the first day of the month
  for (let i = 0; i < firstDay; i++) {
    calendarDays.push(null);
  }
  // Add days of the month
  for (let day = 1; day <= daysInMonth; day++) {
    calendarDays.push(day);
  }

  return (
    <div className="w-full">
      {/* Calendar Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="icon" onClick={goToPreviousMonth}>
            <ChevronLeft className="h-4 w-4" />
          </Button>
          <h2 className="text-2xl font-bold min-w-[200px] text-center">
            {monthNames[month]} {year}
          </h2>
          <Button variant="outline" size="icon" onClick={goToNextMonth}>
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
        <Button variant="outline" onClick={goToToday}>
          Today
        </Button>
      </div>

      {/* Calendar Grid */}
      <div className="border rounded-lg overflow-hidden">
        {/* Day Headers */}
        <div className="grid grid-cols-7 bg-muted">
          {dayNames.map((day) => (
            <div
              key={day}
              className="p-2 text-center text-sm font-medium text-muted-foreground border-r last:border-r-0"
            >
              {day}
            </div>
          ))}
        </div>

        {/* Calendar Days */}
        <div className="grid grid-cols-7">
          {calendarDays.map((day, index) => {
            if (day === null) {
              return (
                <div
                  key={`empty-${index}`}
                  className="aspect-square border-r border-b last:border-r-0 bg-muted/30"
                />
              );
            }

            const holiday = getHoliday(day);
            const today = isToday(day);
            const isHolidayDay = isHoliday(day);
            const isInactiveHoliday = holiday && holiday.isActive === false;

            return (
              <div
                key={day}
                className={cn(
                  "aspect-square border-r border-b last:border-r-0 p-1",
                  "hover:bg-muted/50 transition-colors",
                  today && "bg-blue-50",
                  isHolidayDay && !isInactiveHoliday && "bg-red-50",
                  isInactiveHoliday && "bg-gray-100",
                  !readOnly && "cursor-pointer"
                )}
                onClick={() => !readOnly && handleDateClick(day)}
              >
                <div
                  className={cn(
                    "h-full flex flex-col items-center justify-center rounded",
                    today && "ring-2 ring-blue-500",
                    isHolidayDay && !isInactiveHoliday && "bg-red-100",
                    isInactiveHoliday && "bg-gray-200 opacity-75"
                  )}
                >
                  <span
                    className={cn(
                      "text-sm font-medium",
                      today && "text-blue-600 font-bold",
                      isHolidayDay &&
                        !isInactiveHoliday &&
                        "text-red-600 font-bold",
                      isInactiveHoliday && "text-gray-600",
                      !isHolidayDay && !today && "text-foreground"
                    )}
                  >
                    {day}
                  </span>
                  {holiday && (
                    <span
                      className={cn(
                        "text-[10px] font-medium truncate w-full px-1",
                        isInactiveHoliday ? "text-gray-600" : "text-red-700"
                      )}
                      title={
                        holiday.name + (isInactiveHoliday ? " (Inactive)" : "")
                      }
                    >
                      {holiday.name}
                    </span>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 mt-4 text-sm">
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-blue-50 ring-2 ring-blue-500" />
          <span className="text-muted-foreground">Today</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded bg-red-100" />
          <span className="text-muted-foreground">Public Holiday</span>
        </div>
      </div>
    </div>
  );
}
